<template>
    <div class="Part Statistics Item" :class="{loading: componentData.loading, useHand: this.item.link }" @click.prevent="handleClick">
        <h3 class="h3">{{ this.item.title }}</h3>
        <div class="inner">
            <Icon :name="this.item.icon" />
            <div class="aside">
                <div class="counter">
                    <span class="currency" v-if="currentType === 'EuroField'">€</span>
                    <span class="total">{{ thousands(total) }}</span>
                </div>
                <div class="loader-inline" v-if="componentData.loading"></div>
                <div v-else class="performance" :class="performance_classlist">
                    {{ performance }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {defineComponent,  reactive, watch, inject, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";

// External components
import Icon from "components/icon";
import trans from "helpers/_translation";

import {getDataWithoutLimit} from "helpers/_api";
import { generalFilters, resolveFilters, resolveAppFilters } from "store/filters";
import { user_id } from "store/portal";

// Config for API urls used in this vue template
import { config, apiHostname } from "config";

import {
    date_filters,
    statisticsData,
    startDate,
    endDate,
    startOffset,
    endOffset
} from "store/statistics";

export default defineComponent({
	name: "workflowstatistic-item",
	components: {
        Icon
	},
	props: {
		item: {
			type: Object,
			required: true
		}
	},
	setup(props) {

        const router = useRouter();

        const viewData = inject("viewData");
        const componentData = reactive({
            loading: true,
            all: []
        });


        const filterByDate = (items, start, end) => {
            return items.filter(item => {
                const targetDate = new Date(item.creation_date);
                if (start && (targetDate <= start)) {
                    return false;
                }
                if (end && (targetDate > end)) {
                    return false;
                }
                return true;
            });
        };

        const currentItems = computed(() => {
            // Based on .creation_date filter if within range
            return filterByDate(componentData.all, startDate.value, endDate.value);
        });
        const offsetItems = computed(() => {
            // Based on .creation_date filter if within range
            return filterByDate(componentData.all, startOffset.value, endOffset.value);
        });


        const resolveProperties = (items, property_id) => {
            if (!property_id) {
                return [];
            }
            return items.map(item => {
                return item.extra.workflowproperties.find(a => `${a.field.id}` === `${property_id}`);
            }).filter(a => a);
        };
        const currentProperties = computed(() => {
            // Get all our properties for the current items
            return resolveProperties(currentItems.value, props.item.property_id);
        });
        const offsetProperties = computed(() => {
            // Get all our properties for the offset items
            return resolveProperties(offsetItems.value, props.item.property_id);
        });
        const currentType = computed(() => {
            // Get all our properties for the current items
            if (!props.item.property_id) {
                return "CounterField";
            }
            return currentProperties.value?.[0]?.field?.type;
        });

        const getData = () => {
            if (!user_id.value) return;
            if (!props.item) return;
            componentData.loading = true;
            resolveAppFilters(viewData.extra_filters).then(extra_filters => {
                const filters = resolveFilters([
                    [
                        "FieldFilter",
                        {
                            "column": "state",
                            "value": "Online"
                        }
                    ],
                    ...generalFilters.workflowusergroup,
                    ...extra_filters,
                    ...props.item.filters,
                    ...date_filters(props.item.date_key ?? 'creation_date')
                ]);
                if (viewData.workflowboard_id) {
                    filters.push(...generalFilters.workflowboard[viewData.workflowboard_id]);
                }

                let url = `${apiHostname}/api/v1.0/workflowcard?limit=100`;
                componentData.all = [];
                getDataWithoutLimit(url, filters, componentData, "all").then(() => {
                    componentData.loading = false;
                });
            });
        };

        onMounted(() => {
            getData();
            watch(
                () => [user_id.value, props.item, startDate, endDate],
                () => {
                    if (!user_id.value) return;
                    // @TODO, instead of getting all items
                    // @TODO Get specific properties based on card result by filters passed
                    getData();
                },
                {
                    deep: true
                }
            )
        });



        const count = computed(() => {
            return currentItems.value.length;
        });
        const total = computed(() => {
            // Based on props.item.property_id, sum the values
            if (!props.item.property_id) {
                return currentItems.value.length;
            }
            // Otherwise sum up the values of the properties
            return currentProperties.value.reduce((a, b) => {
                const value = b?.value.replaceAll(".", "").replace(",", ".");
                return a + parseFloat(value);
            }, 0);
        });
        const offsetTotal = computed(() => {
            // Based on props.item.property_id, sum the values
            if (!props.item.property_id) {
                return offsetItems.value.length;
            }
            // Otherwise sum up the values of the properties
            return offsetProperties.value.reduce((a, b) => {
                return a + parseFloat(b.value);
            }, 0);
        });

        const performance_value = computed(() => {
            // Calculate the growth or decline percentage
            if (!total.value || !offsetTotal.value) {
                return 0;
            }

            return ((total.value - offsetTotal.value) / offsetTotal.value) * 100;
        });
        const performance = computed(() => {
            // Round to single decimal + append with %
            return `${performance_value.value.toFixed(1)}%`;
        });
        const performance_classlist = computed(() => {
            return {
                "growth": performance_value.value > 0,
                "decline": performance_value.value < 0
            };
        });

        const numberFormat = new Intl.NumberFormat('nl-NL');
        const thousands = (value) => {
            if (!value) {
                return 0;
            }
            return numberFormat.format(Number(value.toFixed(2)));
        };


        const handleClick = () => {
            if (!props.item.link) return;

            router.push({
                name: props.item.link
            });
        }

		return {
            trans,
            componentData,

            startDate,
            endDate,

            count,
            total,
            offsetTotal,

            performance,
            performance_value,
            performance_classlist,

            currentType,
            currentProperties,
            offsetProperties,

            thousands,

            handleClick
		};
	},
});

</script>